import { Production } from '__types__';
import { Badge } from '../../../design-system/Badge';
import { Skeleton } from '../../../design-system/Skeleton';
import { TrendingUp } from 'lucide-react';
import { rd } from '@passionware/monads';
import {
  getStatusInfo,
  getStatusBadgeVariant,
  getProductionIcon,
  getProductionSubtitle,
  formatProductionDate,
  type ProductionStatus
} from './productionHelpers';
import { cn } from '../../../platform/dom/cn';
import { ProductionResourceCount } from './useProductionResourceCounts';

/** Parent grid columns — must match subgrid rows */
export const productionResourceParentGridColumns = {
  default:
    '1rem auto minmax(0, auto) minmax(0, auto) minmax(0, auto) auto auto',
  sortable:
    '3rem 1rem auto minmax(0, auto) minmax(0, auto) minmax(0, auto) auto auto'
} as const;

export function productionResourceRowClassName(className?: string) {
  return cn(
    'grid grid-cols-subgrid col-span-full gap-4 p-4 border border-border-default rounded-lg items-center w-full min-w-0',
    'data-dragging:opacity-50 data-dragging:shadow-md',
    className
  );
}

export type ProductionResourceRowCellsProps = {
  production: Production;
  statuses: Record<number, ProductionStatus>;
  resourceCount: ProductionResourceCount;
  /** Sortable only: React Aria drag button (`slot="drag"`) */
  dragHandle?: React.ReactNode;
};

export function ProductionResourceRowCells({
  production,
  statuses,
  resourceCount,
  dragHandle
}: ProductionResourceRowCellsProps) {
  const status = getStatusInfo(production.status_id, statuses);
  const statusBadge = status ? getStatusBadgeVariant(status.status_type) : null;
  const subtitle = getProductionSubtitle(production.code);
  const color = production.color;

  return (
    <>
      {dragHandle != null ? (
        <div className="shrink-0 w-6 flex items-center justify-center text-fg-muted">
          {dragHandle}
        </div>
      ) : null}

      {/* Color bar */}
      <div
        className="w-full h-full rounded-full min-w-[2px] min-h-px"
        style={{ backgroundColor: color || 'transparent' }}
        aria-hidden="true"
      />

      {/* Icon */}
      <div className="shrink-0 w-6 flex items-center justify-center text-base leading-none">
        {getProductionIcon(production) || (
          <span className="inline-block w-6" aria-hidden="true" />
        )}
      </div>

      {/* Production name */}
      <div className="text-base font-medium text-fg-default truncate min-w-0">
        {production.title || (
          <span className="text-fg-subtle italic">(no name)</span>
        )}
      </div>

      {/* Subtitle/ID */}
      <div className="text-sm text-fg-muted truncate min-w-0">
        {subtitle || <span className="text-fg-subtle italic">(no code)</span>}
      </div>

      {/* Date */}
      <div className="text-sm text-fg-muted truncate min-w-0">
        {formatProductionDate(production)}
      </div>

      {/* Badge */}
      <div className="shrink-0">
        {statusBadge && (
          <Badge variant={statusBadge.variant} tone="subtle" size="sm">
            {statusBadge.label}
          </Badge>
        )}
      </div>

      {/* Resources count */}
      <div className="flex items-center gap-2 shrink-0">
        <TrendingUp className="size-4 text-blue-600" />
        {rd
          .journey(resourceCount[production.id] ?? rd.ofPending())
          .wait(<Skeleton className="h-4 w-32" />)
          .catch(() => (
            <span className="text-sm text-fg-danger whitespace-nowrap">
              Failed to load
            </span>
          ))
          .map(resourceCount =>
            resourceCount === null ? (
              <span className="text-sm text-fg-danger whitespace-nowrap">
                Failed to load
              </span>
            ) : (
              <span className="text-sm text-fg-muted whitespace-nowrap">
                {resourceCount} {resourceCount === 1 ? 'Resource' : 'Resources'}{' '}
                booked
              </span>
            )
          )}
      </div>
    </>
  );
}
